import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  FlaskConical,
  GraduationCap,
  ListChecks,
  Rocket,
  Target,
  Users,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/Alert';
import { Badge, PublicationBadge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { fetchers, queryKeys } from '@/services/queries';
import { NotFoundPage } from '@/pages/NotFoundPage';

export function TrackDetailPage() {
  const { slug = '' } = useParams();
  const track = useQuery({
    queryKey: queryKeys.track(slug),
    queryFn: () => fetchers.track(slug),
    retry: false,
  });

  if (track.isLoading) return <FullPageSpinner label="Chargement du parcours…" />;
  if (track.isError || !track.data) return <NotFoundPage />;

  const data = track.data;
  const tone = data.code === 'A' ? 'tech' : data.code === 'B' ? 'ai' : 'agent';
  const openModules = data.modules.filter((module) => module.status === 'published').length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Link
        to="/parcours"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au catalogue
      </Link>

      <header className="mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>Parcours {data.code}</Badge>
          <Badge tone="neutral">{data.level}</Badge>
          <Badge tone="neutral">
            <Clock className="h-3 w-3" />
            {data.duration_hours} heures
          </Badge>
        </div>
        <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{data.title}</h1>
        {data.subtitle ? <p className="mt-2 text-lg text-muted">{data.subtitle}</p> : null}
        <p className="mt-5 max-w-3xl text-muted">{data.description}</p>
      </header>

      <dl className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Durée totale', value: `${data.duration_hours} h`, icon: Clock },
          { label: 'Modules', value: data.module_count, icon: BookOpen },
          { label: 'Modules ouverts', value: openModules, icon: CheckCircle2 },
          { label: 'Niveau', value: data.level, icon: GraduationCap },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="p-4">
            <Icon className="h-4 w-4 text-tech" aria-hidden="true" />
            <dd className="mt-2 text-xl font-bold text-fg">{value}</dd>
            <dt className="text-2xs uppercase tracking-wide text-faint">{label}</dt>
          </Card>
        ))}
      </dl>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Programme */}
          <Card>
            <CardHeader>
              <CardTitle as="h2" className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-tech" />
                Programme — {data.modules.length} modules
              </CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <ol className="divide-y divide-border">
                {data.modules.map((module) => (
                  <li key={module.id} className="px-5 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-tech">{module.code}</span>
                          <PublicationBadge status={module.status} />
                        </div>
                        <h3 className="mt-1.5 font-semibold text-fg">{module.title}</h3>
                        <p className="mt-1 text-sm text-muted">{module.summary}</p>
                        {module.technologies.length > 0 ? (
                          <ul className="mt-2.5 flex flex-wrap gap-1.5">
                            {module.technologies.slice(0, 6).map((tech) => (
                              <li
                                key={tech}
                                className="rounded bg-surface-2 px-2 py-0.5 text-2xs text-muted"
                              >
                                {tech}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <span className="text-sm font-semibold tabular-nums text-muted">
                          {module.duration_hours} h
                        </span>
                        {module.status === 'published' ? (
                          <Link
                            to={`/modules/${module.code}`}
                            className="text-xs font-semibold text-tech hover:underline"
                          >
                            Consulter
                          </Link>
                        ) : (
                          <span className="text-2xs text-faint">En préparation</span>
                        )}
                      </div>
                    </div>
                    {module.status === 'published' ? (
                      <div className="mt-3 flex gap-3 text-2xs text-faint">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3 w-3" />
                          {module.chapter_count} chapitre{module.chapter_count > 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <FlaskConical className="h-3 w-3" />
                          {module.lab_count} TP
                        </span>
                        <span>{module.quiz_count} quiz</span>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            </CardBody>
          </Card>

          {data.final_project ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Rocket className="h-4 w-4 text-ai" />
                  Projet final
                </CardTitle>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-muted">{data.final_project}</p>
              </CardBody>
            </Card>
          ) : null}
        </div>

        <aside className="space-y-6">
          {data.objectives.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-tech" />
                  Objectifs pédagogiques
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {data.objectives.map((objective) => (
                    <li key={objective} className="flex gap-2 text-sm text-muted">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden="true" />
                      {objective}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.prerequisites.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Prérequis</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                  {data.prerequisites.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.skills.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Compétences visées</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="flex flex-wrap gap-1.5">
                  {data.skills.map((skill) => (
                    <li
                      key={skill}
                      className="rounded-full bg-surface-2 px-2.5 py-1 text-2xs font-medium text-muted"
                    >
                      {skill}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.audience.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-tech" />
                  Public visé
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                  {data.audience.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.technologies.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Technologies</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="flex flex-wrap gap-1.5">
                  {data.technologies.map((tech) => (
                    <li
                      key={tech}
                      className="rounded border border-border px-2 py-0.5 text-2xs text-muted"
                    >
                      {tech}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {openModules < data.module_count ? (
            <Alert tone="info" title="Ouverture progressive">
              {openModules} module{openModules > 1 ? 's' : ''} sur {data.module_count} est
              {openModules > 1 ? 'sont' : ''} actuellement ouvert aux inscrits. Les autres sont en
              cours de production.
            </Alert>
          ) : null}

          <ButtonLink to="/connexion" className="w-full">
            S'inscrire à ce parcours
          </ButtonLink>
        </aside>
      </div>
    </div>
  );
}
